import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined"
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined"
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined"
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import FacebookOutlinedIcon from "@mui/icons-material/FacebookOutlined"
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined"
import InstagramIcon from "@mui/icons-material/Instagram"
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined"
import LinkedInIcon from "@mui/icons-material/LinkedIn"
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined"
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Autocomplete from "@mui/material/Autocomplete"
import Button from "@mui/material/Button"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Divider from "@mui/material/Divider"
import FormControlLabel from "@mui/material/FormControlLabel"
import InputAdornment from "@mui/material/InputAdornment"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Unstable_Grid2"
import { styled } from "@mui/material/styles"
import { DateTimePicker } from "@mui/x-date-pickers"
import { isValid as isDateValid } from "date-fns"
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import LengthLimitedTextField from "../../../common/components/LengthLimitedTextField"
import Row from "../../../common/components/Row"
import { setErrorFromAPIArray, useIsMobile } from "../../../common/utils"
import { decrementFreeCandidatesRemaining } from "../../users/reducer"
import { RatingEditor } from "../candidates/RatingStars"
import AvatarEditor, { useAvatarEditorState } from "../settings/AvatarEditor"
import {
  CANDIDATE_MAX_STATS,
  useDialogContainer,
  useGoogleAddressAutocomplete,
  useValidateMaxValueErrorState,
  useValidatedPhoneNumber,
  validateSocialLink,
  validateURL,
} from "../utils"
import CRMStageAutocomplete, {
  useCRMStageAutocompleteState,
} from "./CRMStageAutocomplete"
import CandidateAvatar from "./CandidateAvatar"
import DialogHeader from "./DialogHeader"

export default function UpdateCreateCandidateDialog({
  open,
  onClose,
  candidate,
}) {
  const {
    cropPopoverOpen,
    croppedImageBlob,
    croppedImageDataURL,
    setCroppedImageDataURL,
    imageDataURL,
    imageName,
    inputElemRef,
    popoverAnchorElemRef,
    handleCropFinished,
    handleCropPopoverClosed,
    handleEditButtonClicked,
    handleNewImageSelected,
  } = useAvatarEditorState()
  const [name, setName] = useState("")
  const trimmedNameLength = name.trim().length
  const nameError =
    name && trimmedNameLength < 2
      ? "Name must be at least 2 characters"
      : trimmedNameLength > 100
      ? "Name must be at most 100 characters"
      : null
  const [stamp, setStamp] = useState(new Date())
  const [salesVolume, setSalesVolume] = useState(0)
  const {
    error: salesVolumeError,
    validateMaxValue: validateSalesVolume,
    fieldProps: salesVolumeErrorProps,
  } = useValidateMaxValueErrorState(CANDIDATE_MAX_STATS.salesVolume)
  const [homesSold, setHomesSold] = useState(0)
  const {
    error: homesSoldError,
    validateMaxValue: validateHomesSold,
    fieldProps: homesSoldErrorProps,
  } = useValidateMaxValueErrorState(CANDIDATE_MAX_STATS.homesSold)
  const [yearsInBusiness, setYearsInBusiness] = useState(0)
  const {
    error: yearsInBusinessError,
    validateMaxValue: validateYearsInBusiness,
    fieldProps: yearsInBusinessErrorProps,
  } = useValidateMaxValueErrorState(CANDIDATE_MAX_STATS.yearsInBusiness)
  const [email, setEmail] = useState("")
  const [emailError, setEmailError] = useState(null)
  const { phone, setPhone, phoneError, handlePhoneChanged } =
    useValidatedPhoneNumber()
  const [addressInputValue, setAddressInputValue] = useState("")
  const [address, setAddress] = useState(null)
  const { addressOptions, setAddressOptions } =
    useGoogleAddressAutocomplete(addressInputValue)
  const {
    value: facebookLink,
    handleValueChanged: setFacebookLink,
    errorText: facebookError,
  } = useSocialFieldState()
  const {
    value: instagramLink,
    handleValueChanged: setInstagramLink,
    errorText: instagramError,
  } = useSocialFieldState()
  const {
    value: linkedinLink,
    handleValueChanged: setLinkedinLink,
    errorText: linkedinError,
  } = useSocialFieldState()
  const [companyName, setCompanyName] = useState("")
  const {
    crmStage,
    setCRMStage,
    crmStageInputValue,
    setCRMStageInputValue,
    crmStages,
    isLoadingCRMStages,
    populateCurrentCRMStage,
  } = useCRMStageAutocompleteState()
  const {
    value: website,
    handleValueChanged: setWebsite,
    errorText: websiteError,
  } = useURLFieldState()
  const DEFAULT_RATING = 5
  const [rating, setRating] = useState(DEFAULT_RATING)
  const [notes, setNotes] = useState("")
  const [isCreating, setIsCreating] = useState(false)
  const axios = useAuthorizedAxiosClient()
  const isMobile = useIsMobile()
  const dispatch = useDispatch()

  const Container = useDialogContainer()

  function handleSoftClose() {
    onClose()
  }

  function handleEmailChanged(e) {
    setEmail(e.target.value)
    setEmailError(null)
  }

  function handleStatChangedWrapper(setValue, validateFunc) {
    return (e) => {
      const newValue = e.target.value
      validateFunc(newValue)
      setValue(newValue)
    }
  }

  function handleCreateButtonClicked() {
    setIsCreating(true)
    const formData = new FormData()
    if (imageName) {
      formData.append("profile_image", croppedImageBlob, imageName)
    }
    formData.append("candidate_name", name)
    formData.append("stamp", stamp ? stamp.toISOString() : "")
    formData.append("sales_volume_last_year", salesVolume)
    formData.append("homes_sold_last_year", homesSold)
    formData.append("years_in_business", yearsInBusiness)
    formData.append("email", email)
    formData.append("phone", phone)
    formData.append("address", addressInputValue)
    formData.append("facebook_link", facebookLink)
    formData.append("instagram_link", instagramLink)
    formData.append("linkedin_link", linkedinLink)
    formData.append("company_name", companyName)
    formData.append("website", website)
    formData.append("rating", rating)
    formData.append("notes", notes)
    if (candidate) {
      formData.append("crm_stage_write", crmStage.id)
    }

    const method = candidate ? axios.put : axios.post
    const apiURL = candidate
      ? `/crm/api/candidates/${candidate.id}/`
      : "/clients/api/candidates/create/"

    method(apiURL, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then(({ data }) => {
        onClose(data)
        if (!candidate) {
          dispatch(decrementFreeCandidatesRemaining())
        }
      })
      .catch(({ response: { data } }) => {
        setErrorFromAPIArray(data, "email", setEmailError)
      })
      .finally(() => setIsCreating(false))
  }

  useEffect(
    () => {
      if (!candidate) {
        return
      }
      setCroppedImageDataURL(candidate.profile_image)
      setName(candidate.candidate_name || "")
      setStamp(new Date(candidate.stamp))
      setSalesVolume(candidate.sales_volume_last_year || 0)
      setHomesSold(candidate.homes_sold_last_year || 0)
      setYearsInBusiness(candidate.years_in_business || 0)
      setEmail(candidate.email)
      setPhone(candidate.phone || "")
      if (candidate.address) {
        setAddressInputValue(candidate.address)
        setAddress(candidate.address)
        setAddressOptions([candidate.address])
      }
      setFacebookLink(candidate.facebook_link || "")
      setInstagramLink(candidate.instagram_link || "")
      setLinkedinLink(candidate.linkedin_link || "")
      setCompanyName(candidate.company_name || "")
      setWebsite(candidate.website || "")
      setRating(candidate.rating || DEFAULT_RATING)
      setNotes(candidate.notes || "")
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidate]
  )

  useEffect(
    () => {
      if (!open || !candidate || crmStages.length > 0) {
        return
      }
      populateCurrentCRMStage(candidate)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open, candidate]
  )

  return (
    <Container
      open={open}
      onClose={handleSoftClose}
      anchor="right"
      fullScreen={isMobile}
    >
      <DialogHeader
        title={candidate ? "Update Candidate Profile" : "Add Candidate"}
        onClose={handleSoftClose}
      />
      <DialogContent>
        <Column sx={{ rowGap: 4 }}>
          <Row sx={{ justifyContent: "center" }}>
            <AvatarEditor
              cropPopoverOpen={cropPopoverOpen}
              croppedImageDataURL={croppedImageDataURL}
              imageDataURL={imageDataURL}
              inputElemRef={inputElemRef}
              popoverAnchorElemRef={popoverAnchorElemRef}
              handleCropFinished={handleCropFinished}
              handleCropPopoverClosed={handleCropPopoverClosed}
              onEditButtonClicked={handleEditButtonClicked}
              onNewImageSelected={handleNewImageSelected}
            >
              <CandidateAvatar
                candidate={{
                  candidate_name: name || "N A",
                }}
              />
            </AvatarEditor>
          </Row>
          <TitledSection title="Basic Information">
            <InputColumn>
              <TextField
                label="Candidate Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!!nameError}
                helperText={nameError}
                fullWidth
                required
              />
              <DateTimePicker
                label="Appointment Date"
                value={stamp}
                onChange={setStamp}
                size="small"
                sx={{ "& .MuiInputBase-input": { py: 1 } }}
              />
              {candidate && candidate.ui_source === "booking" ? (
                <Typography variant="body2" color="text.secondary">
                  For SmartSetter bookings, use Reschedule on the candidate
                  detail page to keep Airtable, Acuity, and the calendar in
                  sync.
                </Typography>
              ) : null}
            </InputColumn>
          </TitledSection>
          <TitledSection title="Statistics">
            <GridContainer>
              <GridItem>
                <TextField
                  label="Sales Volume"
                  value={salesVolume}
                  onChange={handleStatChangedWrapper(
                    setSalesVolume,
                    validateSalesVolume
                  )}
                  type="number"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyOutlinedIcon />
                      </InputAdornment>
                    ),
                  }}
                  {...salesVolumeErrorProps}
                />
              </GridItem>
              <GridItem>
                <TextField
                  label="Homes Sold"
                  value={homesSold}
                  onChange={handleStatChangedWrapper(
                    setHomesSold,
                    validateHomesSold
                  )}
                  type="number"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HomeOutlinedIcon />
                      </InputAdornment>
                    ),
                  }}
                  {...homesSoldErrorProps}
                />
              </GridItem>
              <GridItem>
                <TextField
                  label="Years in Business"
                  value={yearsInBusiness}
                  onChange={handleStatChangedWrapper(
                    setYearsInBusiness,
                    validateYearsInBusiness
                  )}
                  type="number"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayOutlinedIcon />
                      </InputAdornment>
                    ),
                  }}
                  {...yearsInBusinessErrorProps}
                />
              </GridItem>
            </GridContainer>
          </TitledSection>
          <TitledSection title="Contact Information">
            <GridContainer>
              <GridItem>
                <TextField
                  label="Email"
                  value={email}
                  onChange={handleEmailChanged}
                  error={!!emailError}
                  helperText={emailError}
                  type="email"
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </GridItem>
              <GridItem>
                <TextField
                  label="Phone"
                  value={phone}
                  onChange={(e) => handlePhoneChanged(e.target.value)}
                  error={!!phoneError}
                  helperText={phoneError}
                  type="tel"
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneOutlinedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </GridItem>
              <GridItem>
                <Autocomplete
                  noOptionsText={
                    addressInputValue.length === 0
                      ? "Type to search..."
                      : "Address not found"
                  }
                  autoComplete
                  includeInputInList
                  inputValue={addressInputValue}
                  onInputChange={(e, newValue) =>
                    setAddressInputValue(newValue)
                  }
                  value={address}
                  onChange={(e, newValue) => setAddress(newValue)}
                  options={addressOptions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Address"
                      placeholder="Type to search"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment
                            position="start"
                            sx={{ pl: "6px", marginRight: 0 }}
                          >
                            <PlaceOutlinedIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </GridItem>
              <GridItem>
                <TextField
                  label="Website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  error={!!websiteError}
                  helperText={websiteError}
                  type="url"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LanguageOutlinedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </GridItem>
            </GridContainer>
          </TitledSection>
          <TitledSection title="Socials">
            <GridContainer>
              <GridItem>
                <TextField
                  label="Facebook"
                  value={facebookLink}
                  onChange={(e) => setFacebookLink(e.target.value)}
                  error={!!facebookError}
                  helperText={facebookError}
                  type="url"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <SocialInputAdornment
                        Icon={FacebookOutlinedIcon}
                        domain="facebook.com/"
                      />
                    ),
                  }}
                />
              </GridItem>
              <GridItem>
                <TextField
                  label="Instagram"
                  value={instagramLink}
                  onChange={(e) => setInstagramLink(e.target.value)}
                  error={!!instagramError}
                  helperText={instagramError}
                  type="url"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <SocialInputAdornment
                        Icon={InstagramIcon}
                        domain="instagram.com/"
                      />
                    ),
                  }}
                />
              </GridItem>
              <GridItem>
                <TextField
                  label="LinkedIn"
                  value={linkedinLink}
                  onChange={(e) => setLinkedinLink(e.target.value)}
                  error={!!linkedinError}
                  helperText={linkedinError}
                  type="url"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SocialInputAdornment
                          Icon={LinkedInIcon}
                          domain="linkedin.com/"
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </GridItem>
            </GridContainer>
          </TitledSection>
          <TitledSection title="Misc.">
            <GridContainer>
              {candidate ? (
                <Grid xs={12}>
                  <CRMStageAutocomplete
                    crmStage={crmStage}
                    setCRMStage={setCRMStage}
                    crmStageInputValue={crmStageInputValue}
                    setCRMStageInputValue={setCRMStageInputValue}
                    crmStages={crmStages}
                    isLoadingCRMStages={isLoadingCRMStages}
                  />
                </Grid>
              ) : null}
              <Grid xs={12}>
                <TextField
                  label="Company Name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessOutlinedIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid xs={12}>
                <FormControlLabel
                  label="Rating"
                  labelPlacement="start"
                  control={
                    <RatingEditor
                      rating={rating}
                      setRating={(e, newValue) => setRating(newValue)}
                    />
                  }
                  sx={{
                    ml: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "15px",
                      fontWeight: 400,
                    },
                  }}
                />
              </Grid>
            </GridContainer>
          </TitledSection>
          <LengthLimitedTextField
            label="Notes"
            maxLength={300}
            multiline
            value={notes}
            setValue={setNotes}
          />
        </Column>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleSoftClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isCreating}
          disabled={
            !name ||
            nameError ||
            !isDateValid(stamp) ||
            !email ||
            !phone ||
            emailError ||
            phoneError ||
            facebookError ||
            instagramError ||
            linkedinError ||
            salesVolumeError ||
            homesSoldError ||
            yearsInBusinessError
          }
          onClick={handleCreateButtonClicked}
        >
          {candidate ? "Save" : "Create"}
        </LoadingButton>
      </DialogActions>
    </Container>
  )
}

function TitledSection({ title, children, showDivider = true }) {
  return (
    <Column gap={4}>
      <Typography variant="h5">{title}</Typography>
      {children}
      {showDivider && <Divider sx={{ mt: -1 }} />}
    </Column>
  )
}

function SocialInputAdornment({ Icon, domain }) {
  return (
    <InputAdornment position="start">
      <Row sx={{ gap: 1, alignItems: "center", zIndex: 1 }}>
        <Icon />
        <span>{domain}</span>
      </Row>
    </InputAdornment>
  )
}

const InputColumn = styled(Column)(({ theme }) => ({
  gap: theme.spacing(3),
}))

const GridContainer = styled((props) => (
  <Grid container rowSpacing={3} columnSpacing={2} {...props} />
))(() => ({}))

const GridItem = styled((props) => <Grid xs={12} sm={6} {...props} />)(
  () => ({})
)

function useURLFieldState() {
  const [value, setValue] = useState("")
  const [errorText, setErrorText] = useState("")

  function handleValueChanged(newValue) {
    setValue(newValue)
    if (validateURL(newValue)) {
      setErrorText("")
    } else {
      setErrorText("Please enter a valid URL path")
    }
  }
  return {
    value,
    handleValueChanged,
    errorText,
  }
}

function useSocialFieldState() {
  const [value, setValue] = useState("")
  const [errorText, setErrorText] = useState("")

  function handleValueChanged(newValue) {
    if (validateSocialLink(newValue)) {
      setErrorText("")
    } else {
      setErrorText("Please type the path part only")
    }
    setValue(newValue)
  }

  return {
    value,
    handleValueChanged,
    errorText,
  }
}
